/**
 * ═══════════════════════════════════════════════════════════════════════════
 * G-65 — laying a gift message out on a strip of sticker paper
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * PURE, with the measurer injected, exactly like `receiptLayout.ts` — so the
 * wrapping, the line count and the auto-fit are testable in jest with no DOM.
 *
 * Written fresh rather than bolted onto `receiptLayout.ts`, which is a TICKET
 * layout welded to OD-13's locked footer, its blocks and its logo. A note has
 * none of those; sharing the file would mean one of the two growing `if`s about
 * the other.
 */

export type NoteOrientation = "vertical" | "horizontal";

/** Measures a string at a size, in whatever font the caller has set. */
export type MeasureNoteText = (text: string, px: number) => number;

/** Clear of both edges; thermal heads are unreliable at the rim. */
const MARGIN = 10;
const LINE_HEIGHT = 1.34;

/**
 * How long a VERTICAL strip is allowed to get before the text wraps to a
 * second line — about 11 cm at 203 dpi, roughly a greeting card's width.
 *
 * A guess, and labelled as one: it is the length of paper a florist is willing
 * to peel and stick, which is a physical question nobody can answer from a
 * desk. Settle it on real cards.
 */
export const VERTICAL_WRAP_DOTS = 900;

/**
 * Every size a note can print at, largest first. Auto-fit picks the biggest
 * that still fits ACROSS the paper, because a note that silently loses its last
 * two lines is worse than a slightly smaller one.
 *
 * The top two rungs are reachable ONLY when a florist asks for them with the
 * size control — auto-fit starts at `DEFAULT_SIZE_PX` below.
 */
export const SIZE_LADDER = [64, 52, 44, 36, 30, 24, 20];

/**
 * Where auto-fit starts when nobody has touched the size control.
 *
 * Deliberately NOT the top of the ladder. This is the size slice 3 was tuned to
 * on real paper and on the owner's tablet; making the ladder taller so "bigger"
 * has somewhere to go must not silently reprint every short message larger than
 * what was approved.
 */
export const DEFAULT_SIZE_PX = 44;

export interface NoteTextOp {
  text: string;
  /** Baseline position on the pre-rotation canvas. */
  x: number;
  y: number;
  px: number;
}

/** What auto-fit is allowed to choose between. Both in px. */
export interface NoteSizeBounds {
  /**
   * The smallest size this FACE still prints cleanly at. Auto-fit will not go
   * below it: a message that needs smaller type overflows with a warning
   * rather than arriving as a sticker whose strokes have broken up.
   */
  minPx?: number;
  /**
   * The largest size the florist will accept — the size control.
   *
   * A CEILING, not a fixed size. Auto-fit still shrinks below it when the text
   * does not fit across the paper, so asking for big type can never clip a note
   * that would otherwise have survived. Defaults to `DEFAULT_SIZE_PX`.
   */
  maxPx?: number;
}

export interface LaidOutNote {
  ops: NoteTextOp[];
  /** The canvas to draw on, BEFORE any rotation. */
  width: number;
  height: number;
  /** Chosen by auto-fit. */
  px: number;
  /** True when even the smallest size will not fit across the paper. */
  overflows: boolean;
  lines: string[];
}

/**
 * Greedy wrap, breaking an over-long single token by GRAPHEME rather than by
 * code point.
 *
 * `receiptLayout.wrapText` splits by code point, which separates a combining
 * mark from the letter it belongs to — fine for the Latin product names it was
 * written for, wrong for a message someone wrote by hand. `Intl.Segmenter` is
 * available in Android WebView (Chrome 87+) and in every browser this app
 * supports; the code-point loop is kept as a fallback rather than assumed away.
 */
export function wrapNote(
  text: string,
  maxWidth: number,
  px: number,
  measure: MeasureNoteText,
): string[] {
  const graphemes = (word: string): string[] => {
    const Seg = (
      Intl as unknown as { Segmenter?: new (l?: string, o?: object) => object }
    ).Segmenter;
    if (!Seg) return [...word];
    const seg = new Seg(undefined, { granularity: "grapheme" }) as {
      segment(s: string): Iterable<{ segment: string }>;
    };
    return [...seg.segment(word)].map((s) => s.segment);
  };

  const out: string[] = [];
  for (const paragraph of text.split("\n")) {
    let line = "";
    for (const word of paragraph.split(/\s+/).filter(Boolean)) {
      const candidate = line ? `${line} ${word}` : word;
      if (measure(candidate, px) <= maxWidth) {
        line = candidate;
        continue;
      }
      if (line) out.push(line);
      if (measure(word, px) <= maxWidth) {
        line = word;
        continue;
      }
      // One word wider than the strip: break it, keeping clusters intact.
      let chunk = "";
      for (const g of graphemes(word)) {
        if (measure(chunk + g, px) > maxWidth && chunk) {
          out.push(chunk);
          chunk = g;
        } else {
          chunk += g;
        }
      }
      line = chunk;
    }
    out.push(line);
  }
  return out;
}

/**
 * Lays the message out, choosing the largest size that fits.
 *
 * `widthDots` is the paper. Which dimension it BOUNDS depends on the
 * orientation, and getting that backwards is the whole trick:
 *
 *   horizontal — the paper bounds the LINE LENGTH; lines stack downward and
 *     the paper just gets longer, so nothing can overflow.
 *   vertical — the whole block is turned a quarter turn afterwards, so the
 *     paper bounds the block's HEIGHT, i.e. how many lines there can be. Line
 *     length is free and grows the strip.
 */
export function layoutNote(
  text: string,
  orientation: NoteOrientation,
  widthDots: number,
  measure: MeasureNoteText,
  /**
   * An OBJECT rather than two more positional numbers. `minPx` and `maxPx` are
   * both sizes in px and would sit adjacent, so a swapped pair would compile,
   * lay out cleanly and print the wrong thing — the same silent class as the
   * `rowBytes` bug slice 1 paid for.
   */
  bounds: NoteSizeBounds = {},
): LaidOutNote {
  const { minPx = 0, maxPx = DEFAULT_SIZE_PX } = bounds;
  const trimmed = text.trim();
  const wrapWidth =
    (orientation === "horizontal" ? widthDots : VERTICAL_WRAP_DOTS) - MARGIN * 2;

  const attempt = (px: number) => {
    const lines = trimmed ? wrapNote(trimmed, wrapWidth, px, measure) : [];
    const lineHeight = Math.round(px * LINE_HEIGHT);
    const block = lines.length * lineHeight;
    const longest = lines.reduce((w, l) => Math.max(w, measure(l, px)), 0);
    return { lines, lineHeight, block, longest, px };
  };

  // Only VERTICAL can overflow: it is the orientation whose bounded dimension
  // is the line count. Horizontal always fits, because paper is unbounded
  // in the feed direction.
  const budget = widthDots - MARGIN * 2;
  /**
   * The rungs this face and this request leave available, largest first.
   *
   * The two ways of running out need DIFFERENT answers, which is why this is
   * not one filter:
   *   - a florist asked for smaller type than the face can print cleanly (they
   *     had picked 20 px, then switched to the handwriting face, whose floor is
   *     30) — give them the face's own floor, the smallest it prints cleanly
   *     at, NOT the largest rung on the ladder;
   *   - the face's floor is above every rung — give the largest there is, so a
   *     note still lays out rather than vanishing.
   */
  const aboveFloor = SIZE_LADDER.filter((px) => px >= minPx);
  const withinRequest = aboveFloor.filter((px) => px <= maxPx);
  const sizes =
    withinRequest.length > 0
      ? withinRequest
      : aboveFloor.length > 0
        ? [aboveFloor[aboveFloor.length - 1]]
        : [SIZE_LADDER[0]];
  /*
   * `sizes[0]` is measured ONCE, here. The walk then tries only the rungs
   * below it, and only while the current choice still does not fit.
   *
   * The first draft looped from index 0, so the largest size was measured
   * twice on every call — and vertical is the default orientation while the
   * preview redraws on every keystroke, each `attempt` running the injected
   * measurer once per word. `/code-review` 2026-09-12.
   */
  let chosen = attempt(sizes[0]);
  if (orientation === "vertical") {
    for (let i = 1; i < sizes.length && chosen.block > budget; i += 1) {
      chosen = attempt(sizes[i]);
    }
  }
  const overflows = orientation === "vertical" && chosen.block > budget;

  const contentWidth =
    orientation === "horizontal"
      ? widthDots - MARGIN * 2
      : Math.max(1, Math.ceil(chosen.longest));

  const ops: NoteTextOp[] = chosen.lines.map((line, i) => ({
    text: line,
    // Centred: a gift message reads as a message rather than a label, and a
    // centred strip peels straight whichever way round it goes on the card.
    x: MARGIN + contentWidth / 2,
    y: MARGIN + (i + 1) * chosen.lineHeight,
    px: chosen.px,
  }));

  return {
    ops,
    width: contentWidth + MARGIN * 2,
    height: Math.max(1, chosen.lines.length * chosen.lineHeight + MARGIN * 2),
    px: chosen.px,
    overflows,
    lines: chosen.lines,
  };
}
